import {
    ColDef,
    ValueFormatterParams,
    ValueSetterParams,
} from 'ag-grid-community';
import { translateBlockValue } from 'i18n';
import {
    BudgetProposalWithResourcesT,
    ResourceSoftwareIdAndNameT,
} from 'routes/BudgetModule/config/types';
import { cellStyleCadrage, cellStyleCadrageCentered } from '../style';
import { t } from 'i18next';
import { CadrageSummaryItemT } from '../type';
import { budgetCurrencyFormatter } from 'ui/AgGridReact/formatters';
import { BudgetBys } from 'business/constants';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { CHAPTER_SOFTWARE_ID } from 'routes/BudgetModule/config/constants';

export const getCustomCadrageColDefs = (): ColDef[] => [
    {
        field: 'section',
        headerName: t('adminBudgetSpace.cadrage.section'),
        valueFormatter: (params: ValueFormatterParams) =>
            translateBlockValue(params.value, true),
        sort: 'desc',
        sortIndex: 1,
        flex: 3,
        cellStyle: cellStyleCadrage,
        suppressNavigable: true,
    },
    {
        field: 'budgetFullName',
        headerName: t('adminBudgetSpace.cadrage.budget'),
        valueGetter: ({ data }: { data: BudgetProposalWithResourcesT }) =>
            `${data.budgetCode} - ${data.budgetName}`,
        sort: 'asc',
        sortIndex: 0,
        flex: 3,
        cellStyle: cellStyleCadrage,
        suppressNavigable: true,
    },
    {
        field: 'cadrageByChapter',
        headerName: t('adminBudgetSpace.cadrage.cadrageByChapter', {
            chapter: translateBlockValue(
                CHAPTER_SOFTWARE_ID,
                true,
            ).toLowerCase(),
        }),
        flex: 1,
        cellStyle: cellStyleCadrageCentered,
        cellRenderer: 'toggleCellRenderer',
    },
    {
        field: 'customAmount',
        headerName: t('adminBudgetSpace.cadrage.customAmount'),
        sortable: false,
        flex: 1,
        minWidth: 90,
        cellStyle: cellStyleCadrageCentered,
        cellRenderer: 'actionsCellRenderer',
        valueGetter: ({ data }: { data: BudgetProposalWithResourcesT }) => data,
    },
];

const resourceTextComparator = (
    a: ResourceSoftwareIdAndNameT | undefined,
    b: ResourceSoftwareIdAndNameT | undefined,
): number => {
    if (!a?.name) return -1;
    if (!b?.name) return 1;
    const collator = new Intl.Collator('fr');
    return collator.compare(a.name, b.name);
};

export const getEditCustomCadrageColDefs = (
    by: BudgetBys,
    byChapter: boolean,
): ColDef[] => [
    {
        field: 'resource',
        headerName: translateBlockValue(by, true),
        valueFormatter: (params: ValueFormatterParams) => params.value?.name,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: 'textFloatingFilter',
        suppressFloatingFilterButton: true,
        filterValueGetter: ({ data }: { data: CadrageSummaryItemT | null }) =>
            data ? data.resource.name : null,
        comparator: resourceTextComparator,
        suppressNavigable: true,
        rowGroup: byChapter,
        hide: byChapter,
    },
    {
        field: 'chapter',
        headerName: translateBlockValue(CHAPTER_SOFTWARE_ID, true),
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: 'textFloatingFilter',
        suppressFloatingFilterButton: true,
        suppressNavigable: true,
        hide: !byChapter,
    },
    {
        field: 'baseAmount',
        headerName: t('adminBudgetSpace.cadrage.metricTitle'),
        valueFormatter: budgetCurrencyFormatter,
        aggFunc: 'sum',
        suppressNavigable: true,
        type: 'numericColumn',
    },
    {
        field: 'customAmount',
        headerName: t('adminBudgetSpace.cadrage.customAmount'),
        valueFormatter: budgetCurrencyFormatter,
        editable: true,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
            min: 0,
        },
        valueSetter: (params: ValueSetterParams) => {
            if (params.newValue == null) return false;
            params.data.customAmount = params.newValue;
            return true;
        },
        aggFunc: 'sum',
        type: 'numericColumn',
        cellRenderer: 'editableInputCellRenderer',
    },
];

/**
 * Triggers a tracker event when user add a custom cadrage..
 */
export const trackEventAddCustomCadrage = (budgetProposalName: string) =>
    trackEventBudget(TRACKABLE_EVENTS.budget.settings.cadrage.custom.add, {
        budgetProposalName: budgetProposalName,
    });

/**
 * Triggers a tracker event when user edit a custom cadrage.
 */
export const trackEventEditCustomCadrage = (budgetProposalName: string) =>
    trackEventBudget(TRACKABLE_EVENTS.budget.settings.cadrage.custom.edit, {
        budgetProposalName: budgetProposalName,
    });

/**
 * Triggers a tracker event when user delete a custom cadrage..
 */
export const trackEventDeleteCustomCadrage = (budgetProposalName: string) =>
    trackEventBudget(TRACKABLE_EVENTS.budget.settings.cadrage.custom.delete, {
        budgetProposalName: budgetProposalName,
    });
